import { CheckStatusIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export interface StepperStep {
  readonly label: string;
  readonly description?: string;
}

export interface StepperProps {
  readonly steps: readonly StepperStep[];
  readonly current: number;
  readonly className?: string;
}

export function Stepper({ steps, current, className }: StepperProps) {
  return (
    <ol
      role="list"
      className={cn('grid min-w-0 items-center lg:flex', className)}
      style={{
        gridTemplateColumns:
          steps.length > 1
            ? `repeat(${steps.length - 1}, minmax(0, 1fr)) max-content`
            : 'minmax(0, 1fr)',
      }}
    >
      {steps.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const isLast = i === steps.length - 1;
        return (
          <li
            role="listitem"
            key={i}
            aria-current={active ? 'step' : undefined}
            className={cn(
              'contents lg:flex lg:min-w-0 lg:flex-1 lg:items-center lg:gap-2',
              isLast && 'lg:flex-none',
            )}
          >
            <div
              className="row-start-1 flex min-w-0 items-center lg:contents"
              style={{ gridColumn: i + 1 }}
            >
              <div
                className={cn(
                  'size-7 shrink-0 rounded-full inline-flex items-center justify-center text-[12px] font-bold',
                  'transition-colors duration-(--duration-quick) motion-reduce:transition-none',
                  done
                    ? 'bg-primary text-primary-ink'
                    : active
                      ? 'bg-accent-soft text-accent-text border-2 border-accent'
                      : 'bg-surface-2 text-ink-2 border border-hairline-2',
                )}
                aria-hidden="true"
              >
                {done ? <CheckStatusIcon size={14} color="currentColor" /> : i + 1}
              </div>
              {!isLast ? (
                <div
                  aria-hidden="true"
                  className={cn(
                    'mx-1 h-px min-w-2 flex-1 lg:hidden',
                    done ? 'bg-accent' : 'bg-hairline-2',
                  )}
                />
              ) : null}
            </div>
            <div
              className={cn(
                'flex min-w-0 flex-col',
                active ? 'col-span-full row-start-2 mt-3 lg:mt-0' : 'sr-only lg:not-sr-only',
              )}
            >
              <span
                className={cn(
                  'text-[12.5px] font-semibold break-words lg:truncate',
                  active ? 'text-ink' : 'text-ink-2',
                )}
              >
                {step.label}
              </span>
              {step.description ? (
                <span className="hidden lg:block text-caption text-ink-2 truncate">
                  {step.description}
                </span>
              ) : null}
            </div>
            {!isLast ? (
              <div
                className={cn(
                  'hidden min-w-2 flex-1 mx-3 h-px lg:block',
                  done ? 'bg-accent' : 'bg-hairline-2',
                )}
                aria-hidden="true"
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
